/**
 * Auto-generated reference sections for the Docs page.
 *
 * Each component pulls its data from an existing admin endpoint —
 * nothing is hand-maintained. Add a new node kind in the backend
 * catalog, register a new canonical model, declare a new permission,
 * emit a new audit event_type: it shows up here on next page load
 * with zero docs churn.
 */
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { auditApi } from "@/lib/api-audit";
import { canonicalApi } from "@/lib/api-canonical";
import { usersApi } from "@/lib/api-users";
import { workflowsApi } from "@/lib/api-workflows";
import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="mb-2 text-2xl font-semibold tracking-tight">{children}</h1>
  );
}

function SectionSubtitle({ children }: { children: React.ReactNode }) {
  return <p className="mb-6 text-sm text-muted-foreground">{children}</p>;
}

function Loading() {
  return <div className="py-12 text-sm text-muted-foreground">Loading…</div>;
}

function JsonSchemaBlock({ schema }: { schema: Record<string, unknown> | undefined }) {
  if (!schema) return null;
  return (
    <pre className="mt-2 overflow-x-auto rounded-md border border-border/60 bg-muted/40 p-3 text-xs leading-relaxed">
      {JSON.stringify(schema, null, 2)}
    </pre>
  );
}

// ---------------------------------------------------------------------------
// Nodes reference — from /workflows/catalog
// ---------------------------------------------------------------------------

export function NodesReference() {
  const query = useQuery({
    queryKey: ["docs", "nodes-catalog"],
    queryFn: () => workflowsApi.catalog(),
    staleTime: 5 * 60_000,
  });

  if (query.isLoading) return <Loading />;
  const nodes = query.data?.nodes ?? [];
  const byCategory = nodes.reduce<Record<string, typeof nodes>>((acc, n) => {
    (acc[n.category] ??= []).push(n);
    return acc;
  }, {});

  return (
    <div className="max-w-3xl">
      <SectionTitle>Nodi del workflow</SectionTitle>
      <SectionSubtitle>
        Catalogo chiuso dei {nodes.length} node kind disponibili nel DAG editor,
        derivato direttamente dal codice backend (<code>hub.workflow.catalog</code>).
        Ogni kind ha un JSON Schema config validato al save.
      </SectionSubtitle>

      {Object.entries(byCategory).map(([category, items]) => (
        <section key={category} className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {category}
          </h2>
          <div className="space-y-3">
            {items.map((n) => (
              <details
                key={n.kind}
                className="rounded-md border border-border/60 bg-muted/20 p-3"
              >
                <summary className="flex cursor-pointer items-center gap-2 text-sm">
                  <code className="font-mono text-xs">{n.kind}</code>
                  <span className="font-medium">{n.label}</span>
                </summary>
                <p className="mt-2 text-sm text-muted-foreground">{n.description}</p>
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                    Config schema (JSON Schema)
                  </summary>
                  <JsonSchemaBlock schema={n.config_schema} />
                </details>
              </details>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canonical models reference — from /canonical
// ---------------------------------------------------------------------------

export function CanonicalReference() {
  const query = useQuery({
    queryKey: ["docs", "canonical-all"],
    queryFn: () => canonicalApi.list({ page_size: 100 }),
    select: (p) => p.items,
    staleTime: 5 * 60_000,
  });

  if (query.isLoading) return <Loading />;
  const rows = query.data ?? [];
  const byType = rows.reduce<Record<string, typeof rows>>((acc, r) => {
    (acc[r.canonical_type] ??= []).push(r);
    return acc;
  }, {});

  return (
    <div className="max-w-3xl">
      <SectionTitle>Modelli canonici</SectionTitle>
      <SectionSubtitle>
        Schemi versionati che descrivono le entità di business interne.
        I workflow si bindano a un <code>(canonical_type, version)</code>; le
        versioni <code>active</code> e <code>deprecated</code> sono immutabili.
      </SectionSubtitle>

      <div className="space-y-6">
        {Object.entries(byType).map(([type, versions]) => (
          <section key={type}>
            <h2 className="mb-2 font-mono text-sm font-semibold">{type}</h2>
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Description</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {versions
                  .slice()
                  .sort((a, b) => b.version - a.version)
                  .map((v) => (
                    <tr key={v.version}>
                      <td className="font-mono">v{v.version}</td>
                      <td>
                        <Badge
                          tone={
                            v.status === "active"
                              ? "active"
                              : v.status === "draft"
                                ? "draft"
                                : "deprecated"
                          }
                        >
                          {v.status}
                        </Badge>
                      </td>
                      <td className="text-muted-foreground">
                        {v.description ?? "—"}
                      </td>
                      <td className="text-muted-foreground">
                        {new Date(v.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Permissions reference — from /users/groups/known-permissions
// ---------------------------------------------------------------------------

const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  "workflows:read": "Visualizzare workflow, versioni, execution",
  "workflows:write": "Creare / modificare / cancellare workflow e versioni",
  "workflows:execute": "Avviare test-run, retry, cancel di execution",
  "canonical:read": "Visualizzare schemi canonical (qualsiasi status)",
  "canonical:write": "Creare draft, attivare, deprecare schemi canonical",
  "mappings:read": "Visualizzare entity mappings",
  "mappings:write": "Creare / modificare / cancellare entity mappings; import CSV",
  "vault:read": "Visualizzare metadati dei secret (mai i valori)",
  "vault:write": "Creare / ruotare / cancellare secret nel vault",
  "webhooks:read": "Visualizzare webhook endpoint registrati",
  "webhooks:write": "Creare / modificare / cancellare webhook endpoint",
  "users:manage": "CRUD utenti, gruppi, API key di altri utenti",
  "audit:read": "Visualizzare audit log + export CSV",
};

export function PermissionsReference() {
  const query = useQuery({
    queryKey: ["docs", "known-permissions"],
    queryFn: () => usersApi.knownPermissions(),
    staleTime: 5 * 60_000,
  });

  if (query.isLoading) return <Loading />;
  const perms = query.data?.permissions ?? [];

  return (
    <div className="max-w-3xl">
      <SectionTitle>Permission keys</SectionTitle>
      <SectionSubtitle>
        Lista chiusa delle permission attribuibili ai gruppi. Il gruppo{" "}
        <code>admin</code> bypassa ogni check, gli altri sono filtrati da
        questa enum.
      </SectionSubtitle>
      <table>
        <thead>
          <tr>
            <th>Key</th>
            <th>Descrizione</th>
          </tr>
        </thead>
        <tbody>
          {perms.map((p) => (
            <tr key={p}>
              <td className="font-mono text-xs">{p}</td>
              <td className="text-muted-foreground">
                {PERMISSION_DESCRIPTIONS[p] ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit event types reference — from /audit/facets
// ---------------------------------------------------------------------------

export function AuditEventsReference() {
  const query = useQuery({
    queryKey: ["docs", "audit-facets"],
    queryFn: () => auditApi.facets(),
    staleTime: 60_000,
  });

  if (query.isLoading) return <Loading />;
  const events = query.data?.event_types ?? [];
  const targets = query.data?.target_types ?? [];

  // Group event types by their prefix (text before underscore).
  const byPrefix = events.reduce<Record<string, string[]>>((acc, e) => {
    const prefix = e.split("_")[0] ?? "other";
    (acc[prefix] ??= []).push(e);
    return acc;
  }, {});

  return (
    <div className="max-w-3xl">
      <SectionTitle>Eventi audit</SectionTitle>
      <SectionSubtitle>
        Lista degli <code>event_type</code> emessi finora dal sistema, raggruppati
        per area. Auto-derivata dalle righe presenti in <code>audit_log</code>.
      </SectionSubtitle>

      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Event types
      </h2>
      <div className="space-y-4">
        {Object.entries(byPrefix).map(([prefix, items]) => (
          <div key={prefix}>
            <h3 className="mb-1 font-mono text-xs font-medium">{prefix}*</h3>
            <div className="flex flex-wrap gap-1">
              {items.map((e) => (
                <code
                  key={e}
                  className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs"
                >
                  {e}
                </code>
              ))}
            </div>
          </div>
        ))}
      </div>

      <h2 className="mt-8 mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Target types
      </h2>
      <div className="flex flex-wrap gap-1">
        {targets.map((t) => (
          <code
            key={t}
            className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs"
          >
            {t}
          </code>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// API endpoints reference — from /api/openapi.json
// ---------------------------------------------------------------------------

interface OpenApiOperation {
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: Array<{ name: string; in: string; required?: boolean }>;
  responses?: Record<string, { description?: string }>;
}

interface OpenApiSpec {
  paths: Record<string, Record<string, OpenApiOperation>>;
}

const HTTP_TONE: Record<string, "active" | "draft" | "warning" | "danger" | "neutral"> = {
  get: "draft",
  post: "active",
  put: "warning",
  patch: "warning",
  delete: "danger",
};

export function ApiReference() {
  const query = useQuery({
    queryKey: ["docs", "openapi"],
    queryFn: async () => {
      // The OpenAPI JSON is served at /api/openapi.json (not under /api/admin)
      // so we hit it directly with fetch — the typed `api` client targets
      // /api/admin/ specifically.
      const r = await fetch("/api/openapi.json");
      if (!r.ok) throw new Error("OpenAPI not reachable");
      return r.json() as Promise<OpenApiSpec>;
    },
    staleTime: 5 * 60_000,
  });

  if (query.isLoading) return <Loading />;
  if (query.isError) {
    return <div className="text-sm text-danger">OpenAPI non raggiungibile.</div>;
  }

  const paths = query.data?.paths ?? {};
  // Group operations by first tag (FastAPI sets a tag per router).
  type Op = { path: string; method: string; op: OpenApiOperation };
  const byTag: Record<string, Op[]> = {};
  for (const [path, methods] of Object.entries(paths)) {
    for (const [method, op] of Object.entries(methods)) {
      const tag = (op.tags && op.tags[0]) || "untagged";
      (byTag[tag] ??= []).push({ path, method, op });
    }
  }
  // Hide /api/admin/auth stuff that's obvious; keep everything else.
  const tags = Object.keys(byTag).sort();

  // Reference the api module so the import isn't pruned by linter even
  // though we don't directly use it in this component.
  void api;

  return (
    <div className="max-w-4xl">
      <SectionTitle>API admin reference</SectionTitle>
      <SectionSubtitle>
        Tutti gli endpoint dell'admin API, derivati da{" "}
        <code>/api/openapi.json</code>. Espandi una riga per parametri,
        response codes e descrizione.
      </SectionSubtitle>

      <div className="space-y-6">
        {tags.map((tag) => (
          <section key={tag}>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {tag}
            </h2>
            <div className="space-y-1.5">
              {byTag[tag]
                .sort((a, b) => a.path.localeCompare(b.path))
                .map(({ path, method, op }) => (
                  <details
                    key={`${method}-${path}`}
                    className="rounded-md border border-border/60 bg-muted/20"
                  >
                    <summary className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm">
                      <Badge tone={HTTP_TONE[method] ?? "neutral"}>
                        {method.toUpperCase()}
                      </Badge>
                      <code className="font-mono text-xs">{path}</code>
                      {op.summary && (
                        <span className="ml-auto truncate text-xs text-muted-foreground">
                          {op.summary}
                        </span>
                      )}
                    </summary>
                    <div className="border-t border-border/60 px-3 py-2 text-xs">
                      {op.description && (
                        <p className="mb-2 text-muted-foreground whitespace-pre-wrap">
                          {op.description}
                        </p>
                      )}
                      {op.parameters && op.parameters.length > 0 && (
                        <>
                          <div className="font-medium text-muted-foreground">
                            Parameters
                          </div>
                          <ul className="mb-2 ml-4 list-disc">
                            {op.parameters.map((p) => (
                              <li key={p.name}>
                                <code className="font-mono">{p.name}</code>{" "}
                                <span className="text-muted-foreground">
                                  ({p.in}
                                  {p.required ? ", required" : ""})
                                </span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {op.responses && (
                        <>
                          <div className="font-medium text-muted-foreground">
                            Responses
                          </div>
                          <ul className="ml-4 list-disc">
                            {Object.entries(op.responses).map(([code, r]) => (
                              <li key={code}>
                                <code className="font-mono">{code}</code>{" "}
                                {r.description && (
                                  <span className="text-muted-foreground">
                                    — {r.description}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </details>
                ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
